import React, { useState, useEffect } from 'react';
import { fetchActiveDepartments, GetDemandPlanning, saveDemandPlan, updateDemandPlan, fetchGradeRoles } from '../services/apiService';
import { getDepartmentIdFromToken, getToken, getUserFromToken } from '../services/authService';
import JobDescription from '../components/JobDescription';
import { toast } from "react-hot-toast";
import { DocumentView } from '../components/DocumentView';
import LoadingSpinner from '../components/LoadingSpinner';

export default function DemandPlanPage() {
  const [departments, setDepartments] = useState([]);
  const [demandPlans, setDemandPlans] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAddModal, setShowAddModal] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [gradeRoles, setGradeRoles] = useState([]);

  const [newPlan, setNewPlan] = useState({
    department_id: 0,
    referenceNumber: '',
    forecasted_hires: '',
    reason_for_hire: '',
    yearOfPlan: new Date().getFullYear(),
    role: '',
    grade: '',
    documents: {
      fileName: '',
      fileBase64: '',
      size: 0,
      mimeType: '',
      reference_id: "0",
      process_type: 'ForeCastJobDescription',
      uploaded_by: '',
      uploaded_at: '',
      IsActive: true,
    },
  });

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editPlanId, setEditPlanId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentDocument, setCurrentDocument] = useState(null);
// Add this state variable
const [isJdViewerOpen, setIsJdViewerOpen] = useState(false);
  // Load user data and departments on initial load
  useEffect(() => {
    const initializeData = async () => {
      try {
        const tokenData = getToken();
        setUserRole(tokenData);
        await loadDepartments(tokenData);
        await loadDemandPlan(selectedYear, tokenData);
        const grades = await fetchGradeRoles();
        if (grades && grades.length > 0) {
          setGradeRoles(grades);
        } else {
          setGradeRoles([
            { grade: 'A1', role_name: "CXO's" }, { grade: 'A2', role_name: 'Executive Director' },
            { grade: 'A3', role_name: 'Senior Director' }, { grade: 'A4', role_name: 'Director' },
            { grade: 'A5', role_name: 'Senior Manager' }, { grade: 'A6', role_name: 'Manager' },
            { grade: 'A7', role_name: 'Supervisor' }, { grade: 'A8', role_name: 'Senior Specialist' },
            { grade: 'A9', role_name: 'Specialist' }, { grade: 'A10', role_name: 'Senior Officer' },
            { grade: 'A11', role_name: 'Officer I' }, { grade: 'A12', role_name: 'Officer' },
            { grade: 'A13', role_name: 'Junior Officer' },
            { grade: 'T', role_name: 'Trainee' },
            { grade: 'C', role_name: 'Co-op' },
          ]);
        }
      } catch (err) {
        console.error('Failed to initialize:', err);
        toast.error('Failed to load data');
      } finally {
        setIsPageLoading(false);
      }
    };
    initializeData();
  }, []);

  // Load demand plans when year changes
  useEffect(() => {
    if (userRole) {
      loadDemandPlan(selectedYear, userRole);
    }
  }, [selectedYear, userRole]);

  // Filter data when filters change
  useEffect(() => {
    filterData();
  }, [searchKey, selectedDepartment, demandPlans]);

  const loadDepartments = async (tokenData) => {
    try {
      const deptData = await fetchActiveDepartments();

      if (tokenData?.IsDepartmentRestricted === 'True') {
        const departmentId = getDepartmentIdFromToken();
        const filtered = deptData.filter(
          (dept) => String(dept.department_id) === String(departmentId)
        );
        setDepartments(filtered);
        setSelectedDepartment(departmentId);
        setNewPlan((prev) => ({ ...prev, department_id: departmentId }));
      } else {
        setDepartments([{ department_id: 'All', department_name: 'All' }, ...deptData]);
      }
    } catch (err) {
      console.error('Failed to load departments:', err);
      toast.error('Failed to load departments');
    }
  };

  const loadDemandPlan = async (year, roleData) => {
    try {
      setTableLoading(true);
      console.log(`Loading demand plans for year: ${year}`);
      const dplan = await GetDemandPlanning(year);
      console.log('API response:', dplan);
      
      if (roleData?.IsDepartmentRestricted === 'True') {
        const departmentId = getDepartmentIdFromToken();
        const filtered = dplan.filter((item) => String(item.department_id) === String(departmentId));
        setDemandPlans(filtered);
      } else {
        setDemandPlans(dplan);
      }
    } catch (err) {
      console.error('Failed to load demand plans:', err);
      toast.error('Failed to load demand plans');
    } finally {
      setTableLoading(false);
    }
  };

  const handleYearChange = (year) => {
    console.log('Year changed to:', year);
    setSelectedYear(year);
    setCurrentPage(1); // Reset to first page when year changes
  };

  const filterData = () => {
    let filtered = [...demandPlans];
    
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter((item) => 
        String(item.department_id) === String(selectedDepartment)
      );
    }

    if (searchKey.trim() !== '') {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchKey.toLowerCase())
        )
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleAddNew = () => {
    setIsEditMode(false);
    setIsViewMode(false);
    setEditPlanId(null);
    setNewPlan({
      department_id: userRole?.IsDepartmentRestricted === 'True' ? getDepartmentIdFromToken() : 0,
      forecasted_hires: '',
      reason_for_hire: '',
      referenceNumber: '',
      yearOfPlan: selectedYear, // Use the currently selected year
      role: '',
      grade: '',
      documents: {
        fileName: '',
        fileBase64: '',
        size: 0,
        mimeType: '',
        reference_id: "0",
        process_type: 'ForeCastJobDescription',
        uploaded_by: '',
        uploaded_at: '',
        IsActive: true,
      },
    });
    setCurrentDocument(null);
    setShowAddModal(true);
  };

  const handleEdit = (plan) => {
    setIsEditMode(true);
    setIsViewMode(false);
    setEditPlanId(plan.demand_id);
    
    setNewPlan({
      department_id: plan.department_id,
      forecasted_hires: plan.forecasted_hires,
      reason_for_hire: plan.reason_for_hire,
      yearOfPlan: plan.yearOfPlan,
      role: plan.role,
      grade: plan.grade,
      referenceNumber: plan.referenceNumber,
      documents: plan.documents || {
        fileName: '',
        fileBase64: '',
        size: 0,
        mimeType: '',
        reference_id: "0",
        process_type: 'ForeCastJobDescription',
        uploaded_by: '',
        uploaded_at: '',
        IsActive: true,
      },
    });
    
    setCurrentDocument(plan.documents);
    setShowAddModal(true);
  };

  const handleView = (plan) => {
    setIsViewMode(true);
    setIsEditMode(false);
    setEditPlanId(plan.demand_id);
    
    setNewPlan({
      department_id: plan.department_id,
      forecasted_hires: plan.forecasted_hires,
      reason_for_hire: plan.reason_for_hire,
      yearOfPlan: plan.yearOfPlan,
      role: plan.role,
      grade: plan.grade,
      referenceNumber: plan.referenceNumber,
      documents: plan.documents || {
        fileName: '',
        fileBase64: '',
        size: 0,
        mimeType: '',
        reference_id: "0",
        process_type: 'ForeCastJobDescription',
        uploaded_by: '',
        uploaded_at: '',
        IsActive: true,
      },
    });
    
    setCurrentDocument(plan.documents);
    setShowAddModal(true);
  };

  const handleJobDescriptionFileUpload = (e) => {
    const file = e.target.files[0];
    const validTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg"
    ];

    if (!file) {
      toast.error("No file selected.");
      return;
    }

    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Only PDF or image files are allowed.");
      e.target.value = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB. Please upload a smaller file.");
      e.target.value = null;
      return;
    }

    const userName = getUserFromToken();
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(",")[1];

      setNewPlan((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          fileName: file.name,
          fileBase64: base64String,
          size: file.size,
          mimeType: file.type,
          reference_id: "0",
          process_type: "ForeCastJobDescription",
          uploaded_by: userName,
          uploaded_at: new Date().toISOString(),
          IsActive: true,
        },
      }));
      
      setCurrentDocument({
        fileName: file.name,
        fileBase64: base64String,
        size: file.size,
        mimeType: file.type,
        reference_id: "0",
        process_type: "ForeCastJobDescription",
        uploaded_by: userName,
        uploaded_at: new Date().toISOString(),
        IsActive: true,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSaveNewPlan = async () => {
    if (
      !newPlan.department_id ||
      !newPlan.yearOfPlan ||
      !newPlan.forecasted_hires ||
      !newPlan.reason_for_hire.trim() ||
      !newPlan.role.trim() ||
      !newPlan.grade.trim() ||
      isNaN(parseInt(newPlan.forecasted_hires))
    ) {
      toast.error('Please fill out all required fields correctly.');
      return;
    }

    try {
      setLoading(true);
      const created_by = getUserFromToken();

      const planToSave = {
        ...newPlan,
        forecasted_hires: parseInt(newPlan.forecasted_hires),
        created_by,
      };

      if (isEditMode) {
        const updatedPlan = {
          ...planToSave,
          demand_id: editPlanId,
        };

        await updateDemandPlan(updatedPlan);
        toast.success('Demand plan updated successfully!');
      } else {
        await saveDemandPlan(planToSave);
        toast.success('Demand plan saved successfully!');
      }

      // Reload data for the current year
      await loadDemandPlan(selectedYear, userRole);
      setShowAddModal(false);
      setIsEditMode(false);
      setIsViewMode(false);
      setEditPlanId(null);
    } catch (error) {
      toast.error('Failed to save the demand plan. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredData.length);

  if (isPageLoading) return <LoadingSpinner label="Loading Demand Plan" />;

  return (

          <div className='
      bg-white
      mx-4
      sm:sm:pt-4 sm:px-4 sm:rounded-t-[32px]
      md:md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px]
      lg:lg:px-[calc(6%-1rem)]
      xl:h-[100%] xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px]
      2xl:2xl:px-[calc(8%-1rem)]
      3xl:3xl:px-[calc(8%-1rem)]
      4xl:4xl:px-[calc(10%-1rem)]
    '>

      <div className='mp-6 relative -top-[180px]' >
      {/* Header Section */}
   
       
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-6 rounded-xl" style={{ 
            backgroundColor: 'white',
            border: '1px solid rgba(18, 76, 114, 0.1)',
            boxShadow: '0 4px 12px rgba(18, 76, 114, 0.08)'
          }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Total Plans</p>
                <p className="text-2xl font-bold mt-1" style={{ color: 'rgb(18, 76, 114)' }}>{demandPlans.length}</p>
              </div>
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                <svg className="w-6 h-6" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
              For {selectedYear}
            </p>
          </div>

          <div className="p-6 rounded-xl" style={{ 
            backgroundColor: 'white',
            border: '1px solid rgba(18, 76, 114, 0.1)',
            boxShadow: '0 4px 12px rgba(18, 76, 114, 0.08)'
          }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Selected Year</p>
                <p className="text-2xl font-bold mt-1" style={{ color: 'rgb(18, 76, 114)' }}>{selectedYear}</p>
              </div>
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                <svg className="w-6 h-6" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
              Currently viewing
            </p>
          </div>

          <div className="p-6 rounded-xl" style={{ 
            backgroundColor: 'white',
            border: '1px solid rgba(18, 76, 114, 0.1)',
            boxShadow: '0 4px 12px rgba(18, 76, 114, 0.08)'
          }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Filtered Plans</p>
                <p className="text-2xl font-bold mt-1" style={{ color: 'rgb(18, 76, 114)' }}>{filteredData.length}</p>
              </div>
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                <svg className="w-6 h-6" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </div>
            </div>
            <p className="text-xs mt-2" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
              Matching filters
            </p>
          </div>

            {userRole?.IsDepartmentRestricted !== 'True' && (
               <div 
        onClick={handleAddNew}
          className="cursor-pointer"
        >
       <div 
            className="w-full bg-center bg-cover p-8 text-center rounded-2xl hover:opacity-90 transition-opacity duration-200" 
            style={{ backgroundImage: `url('/add-bg.png')` }}
          >
            <div className='flex flex-col gap-4 items-center'>
              <img src='/add.svg' className='w-[40px]' alt="Add icon" />
              <label className='font-bold text-xl text-white cursor-pointer'>Create New Demand Plan</label>
            </div>
          </div>
        </div>
            
          )}
        
      </div>

      {/* Main Content */}
     
        <div className="rounded-xl p-6" style={{ 
          backgroundColor: 'white',
          border: '1px solid rgba(18, 76, 114, 0.1)',
          boxShadow: '0 4px 20px rgba(18, 76, 114, 0.1)'
        }}>
          {/* Filters Section */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              {/* Department Filter */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                  Department
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg outline-none transition"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgba(18, 76, 114, 0.9)',
                    backgroundColor: 'white'
                  }}
                >
                  {departments.map((dept) => (
                    <option key={dept.department_id} value={dept.department_id}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Filter */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => handleYearChange(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg outline-none transition"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgba(18, 76, 114, 0.9)',
                    backgroundColor: 'white'
                  }}
                  disabled={tableLoading}
                >
                  {yearRange.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {tableLoading && (
                  <p className="text-xs mt-1" style={{ color: 'rgb(18, 76, 114)' }}>
                    Loading data for {selectedYear}...
                  </p>
                )}
              </div>

              {/* Search */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                  Search
                </label>
                <input
                  type="text"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder="Search plans..."
                  className="w-full px-3 py-2 border rounded-lg outline-none transition"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgba(18, 76, 114, 0.9)',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              {/* Items per Page */}
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                  Items per Page
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg outline-none transition"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgba(18, 76, 114, 0.9)',
                    backgroundColor: 'white'
                  }}
                >
                  {[5, 10, 20, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
            <table className="min-w-full">
              <thead style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
                <tr>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Reference</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Department</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Role</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Grade</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Forecasted Hires</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Year</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Status</th>
                  <th className="py-3 px-4 text-left text-sm font-semibold" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-10 h-10 border-3 rounded-full animate-spin mb-4" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', borderTopColor: 'rgb(18, 76, 114)' }} />
                        <p className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                          Loading demand plans for {selectedYear}...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 mb-4" style={{ color: 'rgba(18, 76, 114, 0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm font-medium mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                          No demand plans found
                        </p>
                        <p className="text-xs" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                          {demandPlans.length === 0 
                            ? `No plans exist for ${selectedYear}. ${userRole?.IsDepartmentRestricted !== 'True' ? 'Try a different year or add a new plan.' : 'Try a different year.'}`
                            : 'No plans match your current filters. Try adjusting your search or filters.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredData.slice(startIndex, endIndex).map((item, index) => (
                    <tr key={item.demand_id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.referenceNumber || 'N/A'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.department_name || 'Unknown'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.role || 'N/A'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.grade || 'N/A'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.forecasted_hires || '0'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', color: 'rgba(18, 76, 114, 0.9)' }}>
                        {item.yearOfPlan || 'N/A'}
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                        <span className="px-2 py-1 text-xs rounded-full" style={{ 
                          backgroundColor: item.status === 'Approved' ? 'rgba(34, 197, 94, 0.1)' : 
                                        item.status === 'Draft' ? 'rgba(245, 158, 11, 0.1)' : 
                                        'rgba(18, 76, 114, 0.1)',
                          color: item.status === 'Approved' ? '#22c55e' : 
                                item.status === 'Draft' ? '#f59e0b' : 
                                'rgba(18, 76, 114, 0.9)'
                        }}>
                          {item.status || 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleView(item)}
                            className="px-3 py-1 rounded text-sm transition-colors"
                            style={{ 
                              backgroundColor: 'rgba(18, 76, 114, 0.1)',
                              color: 'rgb(18, 76, 114)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                            }}
                          >
                            View
                          </button>
                          {userRole?.IsDepartmentRestricted !== 'True' && (
                            <button
                              onClick={() => handleEdit(item)}
                              className="px-3 py-1 rounded text-sm transition-colors"
                              style={{ 
                                backgroundColor: 'rgba(18, 76, 114, 0.1)',
                                color: 'rgb(18, 76, 114)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
              <div className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                Showing <span className="font-semibold">{startIndex + 1}-{endIndex}</span> of <span className="font-semibold">{filteredData.length}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border text-sm transition-colors disabled:opacity-50"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgb(18, 76, 114)'
                  }}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
                  const pageNum = currentPage <= 3 ? idx + 1 : 
                                currentPage >= totalPages - 2 ? totalPages - 4 + idx :
                                currentPage - 2 + idx;
                  if (pageNum < 1 || pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === pageNum ? 'font-semibold' : ''}`}
                      style={{ 
                        backgroundColor: currentPage === pageNum ? 'rgb(18, 76, 114)' : 'transparent',
                        color: currentPage === pageNum ? 'white' : 'rgb(18, 76, 114)',
                        border: currentPage === pageNum ? 'none' : '1px solid rgba(18, 76, 114, 0.3)'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border text-sm transition-colors disabled:opacity-50"
                  style={{ 
                    borderColor: 'rgba(18, 76, 114, 0.3)',
                    color: 'rgb(18, 76, 114)'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
       

      {/* Your existing modal component stays here - keep it as is */}
      {showAddModal && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-50 p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl lg:max-w-3xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', borderWidth: '1px' }}>
      {/* Modal Header */}
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ 
        borderColor: 'rgba(18, 76, 114, 0.1)',
        background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
      }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
          {isViewMode ? 'View Demand Plan' : (isEditMode ? 'Edit Demand Plan' : 'Add New Demand Plan')}
        </h2>
        <button 
          onClick={() => {
            setShowAddModal(false);
            setIsViewMode(false);
          }} 
          className="transition-all rounded-full p-1.5"
          style={{ color: 'rgba(18, 76, 114, 0.6)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
          }}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Modal Body */}
      <div className="p-6 max-h-[70vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Form Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Department */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlan.department_id}
                onChange={(e) => setNewPlan({ ...newPlan, department_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                style={{ 
                  borderColor: 'rgba(18, 76, 114, 0.3)',
                  backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                  e.target.style.borderColor = 'rgb(18, 76, 114)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                }}
                disabled={isViewMode}
              >
                <option value="">Select Department</option>
                {departments.filter(d => d.department_id !== 'All').map((dept) => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Grade */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Grade <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlan.grade}
                onChange={(e) => setNewPlan({ ...newPlan, grade: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                style={{
                  borderColor: 'rgba(18, 76, 114, 0.3)',
                  backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                  e.target.style.borderColor = 'rgb(18, 76, 114)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                }}
                disabled={isViewMode}
              >
                <option value="">Select Grade</option>
                {[...gradeRoles].sort((a, b) => {
                  const aNum = parseInt(a.grade.slice(1));
                  const bNum = parseInt(b.grade.slice(1));
                  if (isNaN(aNum) && isNaN(bNum)) return a.grade.localeCompare(b.grade);
                  if (isNaN(aNum)) return 1;
                  if (isNaN(bNum)) return 1;
                  return aNum - bNum;
                }).map((g) => (
                  <option key={g.grade} value={g.grade}>{g.grade} - {g.role_name}</option>
                ))}
              </select>
            </div>

            {/* Forecasted Hires */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Forecasted Hires <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={newPlan.forecasted_hires}
                onChange={(e) => setNewPlan({ ...newPlan, forecasted_hires: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                style={{ 
                  borderColor: 'rgba(18, 76, 114, 0.3)',
                  backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                  e.target.style.borderColor = 'rgb(18, 76, 114)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                }}
                disabled={isViewMode}
                placeholder="Enter number"
              />
            </div>

            {/* Year of Plan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Year of Plan <span className="text-red-500">*</span>
              </label>
              <select
                value={newPlan.yearOfPlan}
                onChange={(e) => setNewPlan({ ...newPlan, yearOfPlan: Number(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                style={{ 
                  borderColor: 'rgba(18, 76, 114, 0.3)',
                  backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                  e.target.style.borderColor = 'rgb(18, 76, 114)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                }}
                disabled={isViewMode}
              >
                {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Role */}
            <div className="space-y-2">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Role <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newPlan.role}
                onChange={(e) => setNewPlan({ ...newPlan, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg outline-none transition text-gray-700"
                style={{
                  borderColor: 'rgba(18, 76, 114, 0.3)',
                  backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                  e.target.style.borderColor = 'rgb(18, 76, 114)';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                }}
                disabled={isViewMode}
                placeholder="Enter role"
              />
            </div>

            {/* Upload Job Description File */}
           {!isEditMode && !isViewMode && (
  <div className="space-y-2">
    <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
      Job Description File
    </label>
    
    {/* File Upload Area */}
    <div className="relative">
      <input
        type="file"
        id="jd-upload"
        accept="application/pdf"
        onChange={handleJobDescriptionFileUpload}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <div 
        className="px-4 py-3 border rounded-lg transition-all"
        style={{ 
          borderColor: 'rgba(18, 76, 114, 0.3)',
          backgroundColor: 'white'
        }}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
            <svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
              {newPlan.documents?.fileName || 'Click to upload job description'}
            </p>
            <p className="text-xs text-gray-500">
              {newPlan.documents?.fileName 
                ? `(${(newPlan.documents.size / 1024).toFixed(2)} KB)` 
                : 'Supports: Only PDF  (Max 5MB)'}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* View and Remove Buttons Row - shown below file upload when file exists */}
    {newPlan.documents?.fileBase64 && (
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={() => setIsJdViewerOpen(true)}
          className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
          style={{ 
            color: 'rgb(18, 76, 114)',
            borderColor: 'rgba(18, 76, 114, 0.3)',
            backgroundColor: 'white'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          View
        </button>
        <button
          type="button"
          onClick={() => {
            setNewPlan((prev) => ({
              ...prev,
              documents: {
                fileName: "",
                fileBase64: "",
                size: 0,
                mimeType: "",
                reference_id: "0",
                process_type: "ForeCastJobDescription",
                uploaded_by: "",
                uploaded_at: "",
                IsActive: false,
              }
            }));
          }}
          className="px-4 py-2.5 border rounded-lg transition-all font-medium flex items-center gap-2"
          style={{ 
            color: '#dc2626',
            borderColor: 'rgba(220, 38, 38, 0.3)',
            backgroundColor: 'white'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Remove
        </button>
      </div>
    )}
  </div>
)}
          </div>

          {/* Reason for Hire */}
          <div className="space-y-2">
            <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
              Reason for Hire <span className="text-red-500">*</span>
            </label>
            <textarea
              value={newPlan.reason_for_hire}
              onChange={(e) => setNewPlan({ ...newPlan, reason_for_hire: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg outline-none transition resize-none h-32 text-gray-700"
              style={{ 
                borderColor: 'rgba(18, 76, 114, 0.3)',
                backgroundColor: isViewMode ? 'rgba(18, 76, 114, 0.05)' : 'white'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
              }}
              readOnly={isViewMode}
              placeholder="Describe the reason for hiring..."
            />
          </div>

          {/* Document / Job Description Section */}
          <div className="space-y-4">
            {isViewMode && newPlan.documents?.fileName && (
              <div className="border rounded-lg p-4" style={{ 
                borderColor: 'rgba(18, 76, 114, 0.2)',
                backgroundColor: 'rgba(18, 76, 114, 0.05)'
              }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Attached Document</span>
                  <span className="text-xs text-gray-600 bg-white px-2 py-1 rounded">{newPlan.documents.fileName}</span>
                </div>
                <DocumentView document={newPlan.documents} />
              </div>
            )}
            
            {!isViewMode && isEditMode && (
              <JobDescription
                activeTab="jd"
                selectedRequest={{ ...newPlan, request_id: newPlan.referenceNumber }}
                isEditMode={isEditMode}
                currentDocument={currentDocument}
                processtype="ForeCastJobDescription"
                onUpload={(file) => {
                  setNewPlan((prev) => ({ ...prev, documents: file }));
                  setCurrentDocument(file);
                }}
                onDelete={() => {
                  setNewPlan((prev) => ({ ...prev, documents: null }));
                  setCurrentDocument(null);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t rounded-b-2xl" style={{ 
        borderColor: 'rgba(18, 76, 114, 0.1)',
        background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))'
      }}>
        <button
          onClick={() => {
            setShowAddModal(false);
            setIsViewMode(false);
          }}
          className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium"
          style={{ 
            color: 'rgb(18, 76, 114)',
            borderColor: 'rgba(18, 76, 114, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Cancel
        </button>
        {!isViewMode && (
          <button
            onClick={handleSaveNewPlan}
            disabled={loading}
            className="px-5 py-2.5 text-white font-medium rounded-lg transition-all"
            style={{ 
              backgroundColor: loading ? 'rgba(18, 76, 114, 0.5)' : 'rgb(18, 76, 114)',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.9)';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              isEditMode ? 'Update Plan' : 'Save Plan'
            )}
          </button>
        )}
      </div>
    </div>
  </div>
)}
{/* Job Description Preview Modal */}
{isJdViewerOpen && (newPlan.documents?.fileBase64 || currentDocument?.fileBase64) && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center z-[10000] p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.3)' }}>
    <div 
      className="relative bg-white rounded-2xl shadow-2xl flex flex-col"
      style={{ 
        borderColor: 'rgba(18, 76, 114, 0.1)', 
        borderWidth: '1px',
        width: '95vw',
        maxWidth: '1000px',
        height: '90vh',
        maxHeight: '700px'
      }}
    >
      {/* Preview Header */}
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ 
        borderColor: 'rgba(18, 76, 114, 0.1)',
        background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)'
      }}>
        <h3 className="text-lg font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
          Job Description Preview
        </h3>
        <button 
          onClick={() => setIsJdViewerOpen(false)}
          className="transition-all rounded-full p-1.5"
          style={{ color: 'rgba(18, 76, 114, 0.6)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'rgba(18, 76, 114, 0.6)';
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-hidden">
        <iframe
          title="Job Description Preview"
          className="w-full h-full border-0"
          src={`data:${newPlan.documents?.mimeType || currentDocument?.mimeType};base64,${newPlan.documents?.fileBase64 || currentDocument?.fileBase64}`}
        />
      </div>
    </div>
  </div>
)}
</div></div>
    
  );
}